// 캔버스 객체(표·텍스트·도형·사진) 편집 상태.
//
// 디자이너 페이지(SignTemplateDesignerPage)가 이미 2,800줄이라 객체 편집은 여기로
// 뺀다. 좌표계는 필드와 같다(캔버스 px, y 전역) — 오버레이 한 장 위에 필드와
// 객체가 함께 놓인다.
//
// ── 잡는 법 (오너 요청 2026-09-22) ─────────────────────────────────────────
// · 표: 칸을 누르면 그 칸이 골라지고 바로 글자를 칠 수 있다(엑셀처럼). 누른 채
//   끌면 범위, Shift+누르기도 범위. 표를 **옮기는** 것은 바깥 테두리를 잡고,
//   **크기**는 네 모서리. 열/행 경계를 끌면 그 열/행만 늘고 준다.
// · 텍스트·도형·사진: 몸통을 끌면 옮겨진다(필드와 같다). 텍스트는 두 번 눌러
//   글자를 고친다. 선은 두 끝점을 끌어 방향·길이를 바꾼다(Shift 로 45° 단위).
//
// ── 바뀜은 한 곳으로 알린다 ────────────────────────────────────────────────
// 모든 바뀜은 `onChange(objects, { coalesce })` 로 올라간다. 페이지가 Undo 이력에
// 넣는다 — 글자 치기처럼 잦은 것은 coalesce 로 묶고, 끌기·크기·패널 조작은 한 번씩.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  LAYOUT_PAGE_H,
  LAYOUT_PAGE_W,
  MIN_LAYOUT_CELL,
  layoutExpandRange,
  layoutLineEnds,
  layoutLineWithEnds,
  layoutMasterOf,
  layoutPageCount,
  layoutResizeTable,
  layoutSetColWidth,
  layoutSetRowHeight,
  newLayoutTable,
  type LayoutLine,
  type LayoutObject,
  type LayoutObjectKind,
  type LayoutTable,
  type LayoutTableCell,
} from '@bhops/shared';
import { applyCellFill, fillPreviewRange } from './cell-fill';

export interface CellPos {
  r: number;
  c: number;
}
export interface CellRange {
  a: CellPos;
  b: CellPos;
}

export type Corner = 'nw' | 'ne' | 'sw' | 'se';

export interface LayoutChangeMeta {
  /** 잦은 편집(글자 치기)이라 이력에서 앞 항목과 묶어도 된다. */
  coalesce: boolean;
}

type Gesture =
  | {
      kind: 'move';
      id: string;
      sx: number;
      sy: number;
      ox: number;
      oy: number;
      moved: boolean;
      /** 여럿을 함께 옮길 때 — 각자의 처음 자리(선은 두 끝점까지). */
      group?: { id: string; x: number; y: number; w: number; h: number; line?: LayoutLine }[];
    }
  | {
      kind: 'resize';
      id: string;
      corner: Corner;
      sx: number;
      sy: number;
      ox: number;
      oy: number;
      ow: number;
      oh: number;
    }
  | { kind: 'col'; id: string; c: number; sx: number; ow: number }
  | { kind: 'row'; id: string; r: number; sy: number; oh: number }
  | { kind: 'line'; id: string; end: 'a' | 'b'; sx: number; sy: number; ox: number; oy: number }
  | { kind: 'cells'; id: string }
  /** 채우기 손잡이(엑셀) — 끄는 동안 지나간 칸. 놓을 때 채운다(cell-fill.ts). */
  | { kind: 'fill'; id: string; target: CellPos | null };

let seq = 0;
function newId(prefix: string): string {
  seq += 1;
  return `${prefix}${Date.now().toString(36)}${seq}`;
}

/** 범위 안의 주인 칸들(덮인 칸은 주인으로 바꿔 한 번씩만). */
export function mastersInRange(t: LayoutTable, range: CellRange): CellPos[] {
  // 화면에 보이는 고른 범위와 같게 — 병합이 걸치면 그만큼 넓힌다(엑셀 규약, 2026-09-23).
  const { r1, r2, c1, c2 } = layoutExpandRange(t, range.a, range.b);
  const seen = new Set<string>();
  const out: CellPos[] = [];
  for (let r = r1; r <= r2; r++) {
    for (let c = c1; c <= c2; c++) {
      const m = layoutMasterOf(t, r, c);
      const key = `${m.r},${m.c}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(m);
    }
  }
  return out;
}

export function isInRange(range: CellRange | null, r: number, c: number): boolean {
  if (!range) return false;
  return (
    r >= Math.min(range.a.r, range.b.r) &&
    r <= Math.max(range.a.r, range.b.r) &&
    c >= Math.min(range.a.c, range.b.c) &&
    c <= Math.max(range.a.c, range.b.c)
  );
}

/** Shift 를 누르고 끌 때 — 다른 끝점을 기준으로 45° 단위에 붙인다(포토샵 규약). */
export function snapAngle(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
): { x: number; y: number } {
  const dx = toX - fromX;
  const dy = toY - fromY;
  const len = Math.hypot(dx, dy);
  if (len === 0) return { x: toX, y: toY };
  const step = Math.PI / 4;
  const ang = Math.round(Math.atan2(dy, dx) / step) * step;
  return { x: fromX + Math.cos(ang) * len, y: fromY + Math.sin(ang) * len };
}

/** 라이브러리에서 고른 사진 — 원본 픽셀 크기를 알면 그 비율로 놓는다. */
export interface PickedImage {
  s3Key: string;
  fileName: string;
  naturalW?: number;
  naturalH?: number;
}

/** 사진을 처음 놓을 때 크기 — 원본 비율을 지키고 300px 안에 들어오게. 모르면 240×160. */
export function fitImageBox(naturalW?: number, naturalH?: number): { w: number; h: number } {
  if (!naturalW || !naturalH) return { w: 240, h: 160 };
  const s = Math.min(1, 300 / naturalW, 300 / naturalH);
  return { w: Math.max(12, Math.round(naturalW * s)), h: Math.max(12, Math.round(naturalH * s)) };
}

export interface UseLayoutEditorArgs {
  zoom: number;
  /** 문서 전체 높이(px) — 끌어도 밖으로 못 나가게. */
  docH: number;
  /** 사진을 고르고(라이브러리) 키를 돌려준다. 취소면 null. */
  pickImage: () => Promise<PickedImage | null>;
  /** 객체가 바뀔 때마다. 페이지가 dirty 표시와 Undo 이력을 맡는다. */
  onChange: (objects: LayoutObject[], meta: LayoutChangeMeta) => void;
}

export function useLayoutEditor({ zoom, docH, pickImage, onChange }: UseLayoutEditorArgs) {
  const [objects, setObjectsState] = useState<LayoutObject[]>([]);
  const objectsRef = useRef(objects);
  objectsRef.current = objects;

  const [selectedObjId, setSelectedObjId] = useState<string | null>(null);
  /**
   * 여러 객체를 함께 고른 것(빈 곳을 끌어 그린 선택 상자, 2026-09-23 오너 요청). 2개 이상일
   * 때만 쓰고, 그동안 `selectedObjId` 는 비어 있다 — 속성 패널·칸 편집은 하나일 때만.
   */
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [cellRange, setCellRange] = useState<CellRange | null>(null);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [placingKind, setPlacingKind] = useState<LayoutObjectKind | null>(null);
  const [gesture, setGesture] = useState<Gesture | null>(null);

  const selected = objects.find((o) => o.id === selectedObjId) ?? null;
  const cellRangeRef = useRef(cellRange);
  cellRangeRef.current = cellRange;

  /** 이력 복원·서버 seed 용 — onChange 를 부르지 않는다. */
  const setObjectsSilently = useCallback((next: LayoutObject[]) => {
    objectsRef.current = next;
    setObjectsState(next);
  }, []);

  const commit = useCallback(
    (
      next: LayoutObject[] | ((prev: LayoutObject[]) => LayoutObject[]),
      meta: LayoutChangeMeta = { coalesce: false },
    ) => {
      const resolved = typeof next === 'function' ? next(objectsRef.current) : next;
      objectsRef.current = resolved;
      setObjectsState(resolved);
      onChange(resolved, meta);
    },
    [onChange],
  );

  const updateObject = useCallback(
    (
      id: string,
      patch: Partial<LayoutObject> | ((o: LayoutObject) => LayoutObject),
      meta?: LayoutChangeMeta,
    ) => {
      commit(
        (prev) =>
          prev.map((o) =>
            o.id !== id
              ? o
              : typeof patch === 'function'
                ? patch(o)
                : ({ ...o, ...patch } as LayoutObject),
          ),
        meta,
      );
    },
    [commit],
  );

  const replaceTable = useCallback(
    (id: string, fn: (t: LayoutTable) => LayoutTable, meta?: LayoutChangeMeta) => {
      commit((prev) => prev.map((o) => (o.id === id && o.kind === 'table' ? fn(o) : o)), meta);
    },
    [commit],
  );

  const deleteObject = useCallback(
    (id: string) => {
      commit((prev) => prev.filter((o) => o.id !== id));
      setSelectedObjId((s) => (s === id ? null : s));
      setGroupIds((g) => g.filter((x) => x !== id));
      setCellRange(null);
      setEditingTextId(null);
    },
    [commit],
  );

  /** 여럿을 한 번에 지운다(되돌리기 한 번에 돌아온다). */
  const deleteObjects = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      const drop = new Set(ids);
      commit((prev) => prev.filter((o) => !drop.has(o.id)));
      setSelectedObjId((s) => (s && drop.has(s) ? null : s));
      setGroupIds([]);
      setCellRange(null);
      setEditingTextId(null);
    },
    [commit],
  );

  const selectObject = useCallback((id: string | null) => {
    setSelectedObjId(id);
    setGroupIds([]);
    setCellRange(null);
    setEditingTextId(null);
  }, []);

  /** 선택 상자로 고른 것 — 0개면 풀고, 1개면 그 하나, 2개 이상이면 묶음. */
  const selectGroup = useCallback((ids: string[]) => {
    setCellRange(null);
    setEditingTextId(null);
    if (ids.length <= 1) {
      setGroupIds([]);
      setSelectedObjId(ids[0] ?? null);
      return;
    }
    setSelectedObjId(null);
    setGroupIds(ids);
  }, []);

  /** 범위 안 주인 칸 모두에 같은 패치. */
  const patchCells = useCallback(
    (
      patch: Partial<LayoutTableCell> | ((c: LayoutTableCell) => LayoutTableCell),
      meta?: LayoutChangeMeta,
    ) => {
      if (!selected || selected.kind !== 'table' || !cellRange) return;
      const targets = mastersInRange(selected, cellRange);
      replaceTable(
        selected.id,
        (t) => ({
          ...t,
          cells: t.cells.map((row, r) =>
            row.map((cell, c) => {
              if (!targets.some((p) => p.r === r && p.c === c)) return cell;
              return typeof patch === 'function' ? patch(cell) : { ...cell, ...patch };
            }),
          ),
        }),
        meta,
      );
    },
    [selected, cellRange, replaceTable],
  );

  /** 캔버스 빈 곳을 눌렀을 때 — 놓기 모드면 객체를 만든다. 만들었으면 true. */
  const placeAt = useCallback(
    async (x: number, y: number): Promise<boolean> => {
      if (!placingKind) return false;
      const px = Math.max(0, Math.round(x));
      const py = Math.max(0, Math.round(y));
      let obj: LayoutObject | null = null;
      switch (placingKind) {
        case 'table': {
          const w = Math.min(480, LAYOUT_PAGE_W - px);
          obj = newLayoutTable(newId('tb'), px, py, 3, 3, w, 32);
          break;
        }
        case 'text':
          obj = {
            kind: 'text',
            id: newId('tx'),
            x: px,
            y: py,
            w: Math.min(240, LAYOUT_PAGE_W - px),
            h: 32,
            text: '텍스트',
            fontSize: 14,
          };
          break;
        case 'shape':
          obj = {
            kind: 'shape',
            id: newId('sh'),
            x: px,
            y: py,
            w: Math.min(200, LAYOUT_PAGE_W - px),
            h: 100,
            shape: 'rect',
            stroke: { width: 1, color: '#222222', style: 'solid' },
            fill: null,
          };
          break;
        case 'image': {
          const picked = await pickImage();
          if (!picked) {
            setPlacingKind(null);
            return true;
          }
          {
            const box = fitImageBox(picked.naturalW, picked.naturalH);
            const w = Math.min(box.w, LAYOUT_PAGE_W - px);
            obj = {
              kind: 'image',
              id: newId('im'),
              x: px,
              y: py,
              w,
              h: box.h,
              s3Key: picked.s3Key,
              fileName: picked.fileName,
            };
          }
          break;
        }
      }
      if (!obj) return false;
      const made = obj;
      commit((prev) => [...prev, made]);
      setPlacingKind(null);
      setSelectedObjId(made.id);
      setCellRange(made.kind === 'table' ? { a: { r: 0, c: 0 }, b: { r: 0, c: 0 } } : null);
      setEditingTextId(made.kind === 'text' ? made.id : null);
      return true;
    },
    [placingKind, pickImage, commit],
  );

  /** 엑셀·서식에서 온 객체를 붙인다. id 는 새로 준다(같은 서식을 두 번 불러도 겹치지 않게). */
  const appendObjects = useCallback(
    (more: LayoutObject[]) => {
      if (more.length === 0) return;
      const fresh = more.map((o) => ({ ...o, id: newId(o.kind.slice(0, 2)) }));
      commit((prev) => [...prev, ...fresh]);
      setSelectedObjId(fresh[0].id);
      setCellRange(null);
    },
    [commit],
  );

  // ── 제스처 시작 ─────────────────────────────────────────────────────────
  const find = (id: string) => objectsRef.current.find((o) => o.id === id);
  const groupIdsRef = useRef(groupIds);
  groupIdsRef.current = groupIds;

  const beginDrag = useCallback((e: React.MouseEvent, id: string) => {
    const o = find(id);
    if (!o) return;
    e.stopPropagation();
    e.preventDefault();
    // 묶음 안의 하나를 잡으면 묶음째 옮긴다.
    if (groupIdsRef.current.includes(id)) {
      const group = groupIdsRef.current
        .map((gid) => find(gid))
        .filter((g): g is LayoutObject => !!g)
        .map((g) => ({
          id: g.id,
          x: g.x,
          y: g.y,
          w: g.w,
          h: g.h,
          line: g.kind === 'shape' && g.line ? { ...g.line } : undefined,
        }));
      (document.activeElement as HTMLElement | null)?.blur?.();
      setGesture({
        kind: 'move',
        id,
        sx: e.clientX,
        sy: e.clientY,
        ox: o.x,
        oy: o.y,
        moved: false,
        group,
      });
      return;
    }
    setGroupIds([]);
    setSelectedObjId(id);
    // 표를 테두리로 잡으면 **표 전체**를 고른 것이다 — 칸 선택(글자 입력 칸)을 풀어야
    // Del 이 칸 글자가 아니라 표를 지운다(2026-09-23 오너 요청).
    setCellRange(null);
    setEditingTextId(null);
    (document.activeElement as HTMLElement | null)?.blur?.();
    setGesture({ kind: 'move', id, sx: e.clientX, sy: e.clientY, ox: o.x, oy: o.y, moved: false });
  }, []);

  const beginResize = useCallback((e: React.MouseEvent, id: string, corner: Corner = 'se') => {
    const o = find(id);
    if (!o) return;
    e.stopPropagation();
    e.preventDefault();
    setGesture({
      kind: 'resize',
      id,
      corner,
      sx: e.clientX,
      sy: e.clientY,
      ox: o.x,
      oy: o.y,
      ow: o.w,
      oh: o.h,
    });
  }, []);

  const beginColResize = useCallback((e: React.MouseEvent, id: string, c: number) => {
    const o = find(id);
    if (!o || o.kind !== 'table') return;
    e.stopPropagation();
    e.preventDefault();
    setGesture({ kind: 'col', id, c, sx: e.clientX, ow: o.colWidths[c] });
  }, []);

  const beginRowResize = useCallback((e: React.MouseEvent, id: string, r: number) => {
    const o = find(id);
    if (!o || o.kind !== 'table') return;
    e.stopPropagation();
    e.preventDefault();
    setGesture({ kind: 'row', id, r, sy: e.clientY, oh: o.rowHeights[r] });
  }, []);

  const beginLineEnd = useCallback((e: React.MouseEvent, id: string, end: 'a' | 'b') => {
    const o = find(id);
    if (!o || o.kind !== 'shape') return;
    e.stopPropagation();
    e.preventDefault();
    const L = layoutLineEnds(o);
    setGesture({
      kind: 'line',
      id,
      end,
      sx: e.clientX,
      sy: e.clientY,
      ox: end === 'a' ? L.x1 : L.x2,
      oy: end === 'a' ? L.y1 : L.y2,
    });
  }, []);

  /** 칸 누르기 — 범위 끌기가 시작된다. Shift 면 앵커를 두고 늘린다. */
  const pickCell = useCallback((tableId: string, r: number, c: number, shift: boolean) => {
    setGroupIds([]);
    setSelectedObjId(tableId);
    setEditingTextId(null);
    setCellRange((prev) =>
      shift && prev ? { a: prev.a, b: { r, c } } : { a: { r, c }, b: { r, c } },
    );
    setGesture({ kind: 'cells', id: tableId });
  }, []);

  /** 채우기 손잡이 누르기 — 고른 범위 오른쪽 아래 작은 네모(2026-10-20 오너 요청). */
  const beginFill = useCallback((e: React.MouseEvent, tableId: string) => {
    if (!cellRangeRef.current) return;
    e.stopPropagation();
    e.preventDefault();
    (document.activeElement as HTMLElement | null)?.blur?.();
    setGesture({ kind: 'fill', id: tableId, target: null });
  }, []);

  /** 칸 위로 지나갈 때 — 끌고 있으면 범위를 늘린다(채우기 손잡이면 채울 자리). */
  const hoverCell = useCallback(
    (tableId: string, r: number, c: number) => {
      if (!gesture || gesture.id !== tableId) return;
      if (gesture.kind === 'fill') {
        setGesture((g) => (g && g.kind === 'fill' ? { ...g, target: { r, c } } : g));
        return;
      }
      if (gesture.kind !== 'cells') return;
      setCellRange((prev) => (prev ? { a: prev.a, b: { r, c } } : prev));
    },
    [gesture],
  );

  // 채우기 손잡이를 끄는 동안 보여 줄 범위.
  const fillRange =
    gesture?.kind === 'fill' && gesture.target && cellRange && selected?.kind === 'table'
      ? fillPreviewRange(selected, cellRange, gesture.target)
      : null;

  // ── 제스처 진행/끝 ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!gesture) return;
    const g = gesture;

    function limitH(prev: LayoutObject[]) {
      return Math.max(docH, layoutPageCount(prev) * LAYOUT_PAGE_H);
    }

    function onMove(e: MouseEvent) {
      if (g.kind === 'cells' || g.kind === 'fill') return;
      // 열 경계는 가로만, 행 경계는 세로만 움직인다.
      const dx = 'sx' in g ? (e.clientX - g.sx) / zoom : 0;
      const dy = 'sy' in g ? (e.clientY - g.sy) / zoom : 0;
      // 묶음 옮기기 — 모두 같은 만큼. 하나라도 종이 밖으로 나가면 거기서 멈춘다.
      if (g.kind === 'move' && g.group) {
        const grp = g.group;
        setObjectsState((prev) => {
          const H = limitH(prev);
          const minDx = -Math.min(...grp.map((m) => m.x));
          const maxDx = Math.min(...grp.map((m) => LAYOUT_PAGE_W - (m.x + m.w)));
          const minDy = -Math.min(...grp.map((m) => m.y));
          const maxDy = Math.min(...grp.map((m) => H - (m.y + m.h)));
          const ddx = Math.round(Math.max(minDx, Math.min(maxDx, dx)));
          const ddy = Math.round(Math.max(minDy, Math.min(maxDy, dy)));
          const by = new Map(grp.map((m) => [m.id, m]));
          const next = prev.map((o) => {
            const m = by.get(o.id);
            if (!m) return o;
            if (o.kind === 'shape' && m.line) {
              return layoutLineWithEnds(o, {
                x1: m.line.x1 + ddx,
                y1: m.line.y1 + ddy,
                x2: m.line.x2 + ddx,
                y2: m.line.y2 + ddy,
              });
            }
            return { ...o, x: m.x + ddx, y: m.y + ddy };
          });
          objectsRef.current = next;
          return next;
        });
        setGesture((cur) =>
          cur && cur.kind === 'move' && !cur.moved ? { ...cur, moved: true } : cur,
        );
        return;
      }
      setObjectsState((prev) => {
        const next = prev.map((o) => {
          if (o.id !== g.id) return o;
          switch (g.kind) {
            case 'move': {
              const nx = Math.max(0, Math.min(LAYOUT_PAGE_W - o.w, Math.round(g.ox + dx)));
              const ny = Math.max(
                0,
                Math.min(Math.max(0, limitH(prev) - o.h), Math.round(g.oy + dy)),
              );
              if (o.kind === 'shape' && o.line) {
                const L = o.line;
                const ddx = nx - o.x;
                const ddy = ny - o.y;
                return layoutLineWithEnds(o, {
                  x1: L.x1 + ddx,
                  y1: L.y1 + ddy,
                  x2: L.x2 + ddx,
                  y2: L.y2 + ddy,
                });
              }
              return { ...o, x: nx, y: ny };
            }
            case 'resize': {
              let x = g.ox;
              let y = g.oy;
              let w = g.ow;
              let h = g.oh;
              if (g.corner === 'se' || g.corner === 'ne') w = g.ow + dx;
              if (g.corner === 'sw' || g.corner === 'nw') {
                w = g.ow - dx;
                x = g.ox + dx;
              }
              if (g.corner === 'se' || g.corner === 'sw') h = g.oh + dy;
              if (g.corner === 'ne' || g.corner === 'nw') {
                h = g.oh - dy;
                y = g.oy + dy;
              }
              const minW =
                o.kind === 'table' ? MIN_LAYOUT_CELL * o.colWidths.length : MIN_LAYOUT_CELL;
              const minH =
                o.kind === 'table' ? MIN_LAYOUT_CELL * o.rowHeights.length : MIN_LAYOUT_CELL;
              if (w < minW) {
                if (g.corner === 'sw' || g.corner === 'nw') x = g.ox + g.ow - minW;
                w = minW;
              }
              if (h < minH) {
                if (g.corner === 'ne' || g.corner === 'nw') y = g.oy + g.oh - minH;
                h = minH;
              }
              if (x < 0) {
                w += x;
                x = 0;
              }
              if (y < 0) {
                h += y;
                y = 0;
              }
              w = Math.min(w, LAYOUT_PAGE_W - x);
              x = Math.round(x);
              y = Math.round(y);
              w = Math.round(w);
              h = Math.round(h);
              if (o.kind === 'table') return { ...layoutResizeTable(o, w, h), x, y };
              return { ...o, x, y, w, h };
            }
            case 'col': {
              if (o.kind !== 'table') return o;
              const room = LAYOUT_PAGE_W - o.x - (o.w - o.colWidths[g.c]);
              return layoutSetColWidth(o, g.c, Math.min(room, g.ow + dx));
            }
            case 'row': {
              if (o.kind !== 'table') return o;
              return layoutSetRowHeight(o, g.r, g.oh + dy);
            }
            case 'line': {
              if (o.kind !== 'shape') return o;
              const L = layoutLineEnds(o);
              let px = Math.max(0, Math.min(LAYOUT_PAGE_W, g.ox + dx));
              let py = Math.max(0, g.oy + dy);
              if (e.shiftKey) {
                const other = g.end === 'a' ? { x: L.x2, y: L.y2 } : { x: L.x1, y: L.y1 };
                const s = snapAngle(other.x, other.y, px, py);
                px = Math.max(0, Math.min(LAYOUT_PAGE_W, s.x));
                py = Math.max(0, s.y);
              }
              const nl = g.end === 'a' ? { ...L, x1: px, y1: py } : { ...L, x2: px, y2: py };
              return layoutLineWithEnds(o, nl);
            }
          }
          return o;
        });
        objectsRef.current = next;
        return next;
      });
      if (g.kind === 'move')
        setGesture((cur) =>
          cur && cur.kind === 'move' && !cur.moved ? { ...cur, moved: true } : cur,
        );
    }

    function onUp() {
      if (g.kind === 'cells') {
        setGesture(null);
        return;
      }
      if (g.kind === 'fill') {
        const t = objectsRef.current.find((o) => o.id === g.id);
        const range = cellRangeRef.current;
        const done =
          t?.kind === 'table' && range && g.target ? applyCellFill(t, range, g.target) : null;
        if (done) {
          const next = objectsRef.current.map((o) => (o.id === g.id ? done.table : o));
          objectsRef.current = next;
          setObjectsState(next);
          onChange(next, { coalesce: false });
          setCellRange(done.range);
        }
        setGesture(null);
        return;
      }
      if (g.kind === 'move' && !g.moved) {
        setGesture(null);
        return;
      }
      onChange(objectsRef.current, { coalesce: false });
      setGesture(null);
    }

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [gesture, zoom, docH, onChange]);

  return {
    objects,
    setObjectsSilently,
    selected,
    selectedObjId,
    selectObject,
    groupIds,
    selectGroup,
    deleteObjects,
    cellRange,
    setCellRange,
    editingTextId,
    setEditingTextId,
    placingKind,
    setPlacingKind,
    placeAt,
    appendObjects,
    updateObject,
    replaceTable,
    patchCells,
    deleteObject,
    beginDrag,
    beginResize,
    beginColResize,
    beginRowResize,
    beginLineEnd,
    pickCell,
    hoverCell,
    beginFill,
    fillRange,
    isDragging: !!gesture && gesture.kind !== 'cells' && gesture.kind !== 'fill',
    isSelectingCells: !!gesture && (gesture.kind === 'cells' || gesture.kind === 'fill'),
  };
}

export type LayoutEditor = ReturnType<typeof useLayoutEditor>;
